import deepEqual from "deep-equal";
import type { ColumnValues, Database, RowIndex } from "../../common/Database";
import type { TableName } from "../../common/database-strings";
import type { DocumentData } from "../../common/Database/core";
import { getDeviceID } from "../../common/device-id";
import type { BaseRowIndex } from "../../common/firebase-function-types";
import type { TypeSchema } from "../../common/description";
import { findTable } from "../../common/description";
import { GlideDateTime } from "../../common/glide-date-time";
import { exceptionToError } from "../../common/Support";
import type { AddRowQueueItem, TableMutationHandler } from "../types";
import type { PrimitiveValue } from "../../common/components/computation-types";
import { convertColumnNamesToFieldNames } from "../../common/components/datastore/field-names";
import type { AppFacilities, OfflineQueue } from "../../common/components/types";

export interface DurableStorageController {
    addRowToTable(
        tableName: TableName,
        rowIndex: RowIndex | undefined,
        values: Record<string, PrimitiveValue>,
        fromDataEditor: boolean
    ): Promise<boolean>;
    setColumnsInRow(
        tableName: TableName,
        rowIndex: RowIndex,
        values: Record<string, PrimitiveValue>,
        withDebounce: boolean,
        fromDataEditor: boolean,
        onCompletion?: () => void
    ): Promise<void>;
    deleteRowAtIndex(
        tableName: TableName,
        rowIndex: RowIndex,
        fromDataEditor: boolean,
        onConfirm: ((serial: number) => void) | undefined
    ): Promise<void>;

    handleUserAgnosticDocumentUpdate(
        tableName: TableName,
        rowIndex: BaseRowIndex,
        rowVersion: number,
        doc: DocumentData
    ): DocumentData;
    handleUserSpecificDocumentUpdate(
        tableName: TableName,
        rowIndex: BaseRowIndex,
        rowVersion: number,
        doc: DocumentData
    ): DocumentData;

    handleDocumentDeletion(tableName: TableName, rowIndex: RowIndex, version: number): void;
    handleUserSpecificDataDeletion(tableName: TableName, rowIndex: RowIndex): DocumentData | undefined;

    resetUserSpecificData(): void;

    appEnvironmentUpdated(): void;
    appUserChanged(): void;

    // We need this to support quota resets.
    purgeConfirmedEntries(): void;
    retire(): void;
}

export interface DataStoreEnvironmentAccessors {
    database(): Database;
    appID(): string;
    appUserID(): string | undefined;
    appFacilities(): AppFacilities;
    tableMutationHandler(): Promise<TableMutationHandler>;
    schema(): TypeSchema;
    makeOfflineQueue(name: string, onOnline: () => void): OfflineQueue<AddRowQueueItem> | undefined;

    userAgnosticColumnsForTable(tableName: TableName): readonly string[];
    userSpecificColumnsForTable(tableName: TableName): readonly string[];
}

export interface DebounceContextFreeArguments {
    readonly values: ColumnValues;
    readonly fromBuilder: boolean;
    readonly fromDataEditor: boolean;
    readonly jobID: string | undefined;
}

export interface DebounceKey {
    readonly appID: string;
    readonly appUserID: string | undefined;
    readonly tableName: TableName;
    readonly rowIndex: RowIndex;
    readonly executionTarget: (key: DebounceKey, value: DebounceContextFreeArguments) => Promise<string>;
}

interface DebounceValue extends DebounceContextFreeArguments {
    readonly pendingPromises: { resolve: (id: string) => void; reject: (e: Error) => void }[];
    readonly timeoutID: ReturnType<typeof setTimeout>;
}

export type DebounceMap = Map<DebounceKey, DebounceValue>;

interface DebounceMapArguments extends DebounceKey, DebounceContextFreeArguments {
    readonly debounceTimeout: number;
    readonly onEntryCreation?: () => Promise<void>;
}

export abstract class DurableStorageControllerBase implements DurableStorageController {
    private readonly _debounceEntries: DebounceMap = new Map();

    constructor(
        protected readonly _accessors: DataStoreEnvironmentAccessors,
        private readonly _deviceIDOverride: string | undefined,
        protected readonly isBuilder: boolean
    ) {}

    public abstract addRowToTable(
        tableName: TableName,
        rowIndex: RowIndex | undefined,
        values: Record<string, PrimitiveValue>,
        fromDataEditor: boolean
    ): Promise<boolean>;
    public abstract setColumnsInRow(
        tableName: TableName,
        rowIndex: RowIndex,
        values: Record<string, PrimitiveValue>,
        withDebounce: boolean,
        fromDataEditor: boolean,
        onCompletion?: () => void
    ): Promise<void>;
    public abstract deleteRowAtIndex(
        tableName: TableName,
        rowIndex: RowIndex,
        fromDataEditor: boolean,
        onConfirm: ((serial: number) => void) | undefined
    ): Promise<void>;

    public abstract handleUserAgnosticDocumentUpdate(
        tableName: TableName,
        rowIndex: BaseRowIndex,
        rowVersion: number,
        doc: DocumentData
    ): DocumentData;
    public abstract handleUserSpecificDocumentUpdate(
        tableName: TableName,
        rowIndex: BaseRowIndex,
        rowVersion: number,
        doc: DocumentData
    ): DocumentData;

    public abstract handleDocumentDeletion(tableName: TableName, rowIndex: RowIndex, version: number): void;

    public abstract handleUserSpecificDataDeletion(tableName: TableName, rowIndex: RowIndex): DocumentData | undefined;

    public abstract resetUserSpecificData(): void;

    public abstract retire(): void;

    protected get deviceID(): string {
        return this._deviceIDOverride ?? getDeviceID();
    }

    protected convertColumnValuesToFields(tableName: TableName, values: ColumnValues): DocumentData {
        const table = findTable(this._accessors.schema(), tableName);
        if (table === undefined) return values;
        const named = convertColumnNamesToFieldNames(new Map(), table, values);
        const ret: DocumentData = {};
        for (const [key, value] of Object.entries(named)) {
            ret[key] = value instanceof GlideDateTime ? value.asDate() : value;
        }
        return ret;
    }

    protected async debounceOperation(args: DebounceMapArguments): Promise<string> {
        const {
            appID,
            appUserID,
            tableName,
            rowIndex,
            executionTarget,
            values,
            fromDataEditor,
            debounceTimeout,
            onEntryCreation,
            jobID,
        } = args;
        const prior = Array.from(this._debounceEntries).find(
            ([key]) =>
                key.appID === appID &&
                key.appUserID === appUserID &&
                deepEqual(key.tableName, tableName, { strict: true }) &&
                deepEqual(key.rowIndex, rowIndex, { strict: true }) &&
                key.executionTarget === executionTarget
        );

        const executeAfterDebounce = async (innerMap: DebounceMap, key: DebounceKey) => {
            const value = innerMap.get(key);
            if (value === undefined) return;
            const { executionTarget: innerExecutionTarget } = key;
            const { pendingPromises } = value;
            innerMap.delete(key);
            try {
                const result = await innerExecutionTarget(key, value);
                for (const { resolve } of pendingPromises) {
                    resolve(result);
                }
            } catch (e: unknown) {
                for (const { reject } of pendingPromises) {
                    reject(exceptionToError(e));
                }
            }
        };

        if (onEntryCreation !== undefined && prior === undefined) {
            await onEntryCreation();
        }

        return new Promise((resolve, reject) => {
            if (prior === undefined) {
                const mapKey: DebounceKey = {
                    appID,
                    appUserID,
                    tableName,
                    rowIndex,
                    executionTarget,
                };
                const mapValue: DebounceValue = {
                    values,
                    fromBuilder: this.isBuilder,
                    fromDataEditor,
                    pendingPromises: [{ resolve, reject }],
                    timeoutID: setTimeout(async () => {
                        await executeAfterDebounce(this._debounceEntries, mapKey);
                    }, debounceTimeout),
                    jobID,
                };
                this._debounceEntries.set(mapKey, mapValue);
            } else {
                const [mapKey, priorValue] = prior;
                clearTimeout(priorValue.timeoutID);

                const mapValue: DebounceValue = {
                    values: { ...priorValue.values, ...values },
                    fromBuilder: this.isBuilder || priorValue.fromBuilder,
                    fromDataEditor: fromDataEditor || priorValue.fromDataEditor,
                    pendingPromises: [...priorValue.pendingPromises, { resolve, reject }],
                    timeoutID: setTimeout(async () => {
                        await executeAfterDebounce(this._debounceEntries, mapKey);
                    }, debounceTimeout),
                    jobID,
                };
                this._debounceEntries.set(mapKey, mapValue);
            }
        });
    }

    public appEnvironmentUpdated(): void {
        return;
    }

    public appUserChanged(): void {
        return;
    }

    public purgeConfirmedEntries(): void {
        // Generally, implementors do not need to support this.
    }
}
